import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  Package,
  Search,
  Plus,
  Minus,
  Trash2,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { showError, showSuccess } from "../../../utils/toastUtils";

const RequestProductsModal = ({
  isOpen,
  onClose,
  bookingId,
  staffId,
  onSuccess,
}) => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      setSelectedProducts([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-all`
      );
      setProducts(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      showError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchTerm.toLowerCase()) 
  );

  const handleAddProduct = (product) => {
    const exists = selectedProducts.find((p) => p._id === product._id);
    if (exists) {
      showError("Product already added");
      return;
    }

    setSelectedProducts([
      ...selectedProducts,
      {
        ...product,
        requestedQuantity: 1,
      },
    ]);
  };

  const handleQuantityChange = (productId, change) => {
    setSelectedProducts((prev) =>
      prev.map((p) => {
        if (p._id === productId) {
          const newQty = p.requestedQuantity + change;
          if (newQty < 1) return p;
          return { ...p, requestedQuantity: newQty };
        }
        return p;
      })
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p._id !== productId));
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      showError("Please select at least one product");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/products-assignment/request-products`,
        {
          bookingId,
          staffId,
          products: selectedProducts.map((p) => ({
            productId: p._id,
            requestedQuantity: p.requestedQuantity,
          })),
        }
      );

      showSuccess("Product request submitted successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to submit request:", error);
      showError(
        error?.response?.data?.message || "Failed to submit product request"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white min-h-40 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-primaryColor/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <ShoppingCart size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                Request Products
              </h2>
              <p className="text-xs text-blue-100">
                Select products you need for this job
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition text-white"
          >
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 min-h-[60vh] flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" size={40} />
          </div>
        ) : (
          <>
            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, brand, or service..."
                  className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primaryColor"
                />
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>

              {/* Available Products */}
              <div>
                <h3 className="text-sm font-bold text-primaryTextColor mb-3">
                  Available Products
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {filteredProducts.length === 0 ? (
                    <div className="col-span-2 text-center py-8 text-gray-500">
                      <Package
                        size={40}
                        className="mx-auto mb-2 text-gray-300"
                      />
                      <p className="text-sm">No products found</p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isSelected = selectedProducts.find(
                        (p) => p._id === product._id
                      );

                      return (
                        <div
                          key={product._id}
                          className={`flex items-center gap-3 p-3 border rounded-lg transition ${
                            isSelected
                              ? "border-primaryColor/50 bg-primaryColor/10"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          {product.productImg ? (
                            <img
                              src={product.productImg}
                              alt={product.productName}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Package size={20} className="text-gray-400" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {product.productName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {product.brand} • Stock: {product.quantity}
                            </p>
                          </div>

                          <button
                            onClick={() => handleAddProduct(product)}
                            disabled={isSelected || product.quantity < 1}
                            className="p-2 bg-primaryColor/80 text-white rounded-lg hover:bg-primaryColor transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {isSelected ? (
                              <span className="text-xs px-1">Added</span>
                            ) : (
                              <Plus size={16} />
                            )}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-primaryTextColor mb-3">
                    Requested Products ({selectedProducts.length})
                  </h3>
                  <div className="space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {selectedProducts.map((product) => (
                      <div
                        key={product._id}
                        className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                      >
                        {product.productImg ? (
                          <img
                            src={product.productImg}
                            alt={product.productName}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Package size={20} className="text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {product.productName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {product.brand}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleQuantityChange(product._id, -1)
                            }
                            className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                          >
                            <Minus size={14} />
                          </button>

                          <span className="w-10 text-center font-semibold text-sm">
                            {product.requestedQuantity}
                          </span>

                          <button
                            onClick={() => handleQuantityChange(product._id, 1)}
                            className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                          >
                            <Plus size={14} />
                          </button>
                        </div>

                        <button
                          onClick={() => handleRemoveProduct(product._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t bg-gray-50 flex gap-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="flex-1 px-3 py-3 border text-sm border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || selectedProducts.length === 0}
                className="flex-1 px-3 py-3 bg-primaryColor/80 text-sm text-white font-medium rounded-lg hover:bg-primaryColor transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <ShoppingCart size={18} />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestProductsModal;
